import React, { useRef, useState, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import { useLoader } from '../../../contexts/LoaderContext';
import ArticleWrapper from './components/ArticleWrapper';

const Article = ({ articleHook }) => {
  const toast = useRef(null);
  const { showLoader, hideLoader } = useLoader();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('DRAFT');
  const [editingArticle, setEditingArticle] = useState(null);

  // Destructure the hook values and functions
  const {
    shopType,
    articles: savedArticles = [],
    loading: articlesLoading = false,
    saveArticle,
    deleteArticle,
    getArticleById,
    getArticles
  } = articleHook || {};

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setShowForm(true);
    // Reset status when creating a new article
    if (!editingArticle) {
      setSelectedStatus('DRAFT');
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedTemplate(null);
    setEditingArticle(null);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const statusItems = [
    {
      label: 'Save as Draft',
      icon: 'pi pi-save',
      command: () => handleStatusChange('DRAFT')
    },
    {
      label: 'Publish',
      icon: 'pi pi-check',
      command: () => handleStatusChange('PUBLISHED')
    }
  ];

  const handleSave = async (values, { setSubmitting }) => {
    if (!saveArticle) {
      console.error('saveArticle function not available');
      return;
    }

    showLoader('Saving article...');
    setSubmitting?.(true);

    try {
      const articleData = {
        ...values,
        ...(editingArticle?.id && { id: editingArticle.id })
      };
      const result = await saveArticle(articleData, selectedTemplate);

      if (result?.success) {
        // Refetch articles to get the latest data
        if (getArticles) {
          await getArticles();
        }
        handleCloseForm();
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Article saved successfully!',
          life: 3000
        });
        handleCloseForm();
      } else {
        throw new Error(result?.error || 'Failed to save article');
      }
    } catch (error) {
      console.error('Error saving article:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to save article. Please try again.',
        life: 3000
      });
    } finally {
      setSubmitting?.(false);
      hideLoader();
    }
  };

  const handleDelete = async (id) => {
    if (!deleteArticle) {
      console.error('deleteArticle function not available');
      return;
    }

    try {
      const success = await deleteArticle(id);

      if (success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Article deleted successfully!',
          life: 3000
        });
      } else {
        throw new Error('Failed to delete article');
      }
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to delete article. Please try again.',
        life: 3000
      });
    }
  };

  const handleEdit = (article) => {
    if (!getArticleById) {
      console.error('getArticleById function not available');
      return;
    }

    // Set the editing article and its status
    setEditingArticle(article);
    if (article.status) {
      setSelectedStatus(article.status);
    } else {
      setSelectedStatus('DRAFT');
    }

    // Show the form with the selected template
    setSelectedTemplate(article.templateType);
    setShowForm(true);
  };

  const getStatusButtonLabel = (status) => {
    return status === 'PUBLISHED' ? 'Publish' : 'Save as Draft';
  };

  const getStatusIcon = (status) => {
    return status === 'PUBLISHED' ? 'pi pi-check' : 'pi pi-save';
  };

  return (
    <div className="article-page">
      <Toast ref={toast} position="top-right" />
      <ArticleWrapper
        shopType={shopType}
        articles={savedArticles}
        loading={articlesLoading}
        showForm={showForm}
        selectedTemplate={selectedTemplate}
        onTemplateSelect={handleTemplateSelect}
        onCloseForm={handleCloseForm}
        onSave={handleSave}
        onDelete={handleDelete}
        onEdit={handleEdit}
      >
        {null}
      </ArticleWrapper>
    </div>
  );
};

export default Article;
